import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Print as PrintIcon,
  Refresh as RefreshIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import ExpenseService from '../../services/expenseService';
import accountingDataFetchService from '../../services/accountingDataFetchService';
import StaffSalaryService from '../../services/staffSalaryService';

const ExpensesTab = () => {
  // Redux state
  const schooldetails = useSelector(state => state.schooldetails || []);
  const currentSchool = schooldetails.length > 0 ? schooldetails[0] : null;
  
  // Local state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [expenseData, setExpenseData] = useState(null);
  
  // Data state
  const [expenses, setExpenses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  
  // Refs to prevent infinite loops
  const isCalculatingRef = useRef(false);

  // Calculate date range based on period
  const dateRange = React.useMemo(() => {
    const today = new Date();
    let start = new Date();
    let end = new Date(today);
    end.setHours(23, 59, 59, 999);

    switch (selectedPeriod) {
      case 'current':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        break;
      case 'quarter':
        const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        start = quarterStart;
        start.setHours(0, 0, 0, 0);
        break;
      case 'year':
        start = new Date(today.getFullYear(), 0, 1);
        start.setHours(0, 0, 0, 0);
        break;
      case 'custom':
        start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
    }

    return { start, end };
  }, [selectedPeriod, startDate, endDate]);

  // Fetch data from database
  const fetchData = async () => {
    if (!currentSchool?.schoolID) {
      setError('No school selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await accountingDataFetchService.fetchAllAccountingData(currentSchool.schoolID);
      setExpenses(data.expenses || []);
      setTeachers(data.teachers || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate expense data
  useEffect(() => {
    if (isCalculatingRef.current) return;
    if (!currentSchool?.schoolID) return;

    const calculateExpenses = () => {
      if (isCalculatingRef.current) return;
      isCalculatingRef.current = true;

      try {
        const { start, end } = dateRange;

        // Calculate staff salaries from payment records
        const staffSalaries = StaffSalaryService.calculateStaffSalaries(teachers, start, end);

        // Calculate expense breakdown
        const expenseBreakdown = ExpenseService.getExpenseBreakdown(
          expenses,
          [], // Salaries calculated separately
          start,
          end
        );

        // Add staff salaries to expenses
        const totalExpenses = expenseBreakdown.total + staffSalaries.total;
        
        // Update the byCategory to include staff salaries
        if (!expenseBreakdown.byCategory['Salaries & Wages']) {
          expenseBreakdown.byCategory['Salaries & Wages'] = 0;
        }
        expenseBreakdown.byCategory['Salaries & Wages'] = staffSalaries.total;

        // Organize expense breakdown
        const expenseBreakdownFormatted = {
          salaries: staffSalaries.total || 0, // Add salaries
          utilities: expenseBreakdown.byCategory['Utilities'] || 0,
          maintenance: expenseBreakdown.byCategory['Maintenance & Repairs'] || 0,
          supplies: expenseBreakdown.byCategory['Supplies'] || 0,
          administrative: expenseBreakdown.byCategory['Administrative'] || 0,
          rent: expenseBreakdown.byCategory['Rent & Leasing'] || 0,
          transportation: expenseBreakdown.byCategory['Transportation'] || 0,
          insurance: expenseBreakdown.byCategory['Insurance'] || 0,
          marketing: expenseBreakdown.byCategory['Marketing'] || 0,
          other: expenseBreakdown.byCategory['Other'] || 0,
          total: totalExpenses // Include salaries in total
        };

        setExpenseData({
          summary: {
            totalExpenses: totalExpenses, // Updated total
            expenseCount: expenseBreakdown.count + staffSalaries.staffCount, // Include staff count
            averageExpense: (expenseBreakdown.count + staffSalaries.staffCount) > 0 
              ? totalExpenses / (expenseBreakdown.count + staffSalaries.staffCount)
              : 0
          },
          breakdown: expenseBreakdownFormatted,
          byCategory: expenseBreakdown.byCategory, // Now includes Salaries & Wages
          byVendor: expenseBreakdown.byVendor,
          byTerm: expenseBreakdown.byTerm,
          staffSalaries: staffSalaries, // Add detailed salary data
          period: {
            start: dateRange.start.toISOString().split('T')[0],
            end: dateRange.end.toISOString().split('T')[0]
          }
        });
      } catch (err) {
        console.error('Error calculating expenses:', err);
        setError('Failed to calculate expenses');
      } finally {
        isCalculatingRef.current = false;
      }
    };

    calculateExpenses();
  }, [expenses, teachers, dateRange, currentSchool?.schoolID]);

  // Load data on mount
  useEffect(() => {
    if (currentSchool?.schoolID) {
      fetchData();
    }
  }, [currentSchool?.schoolID]);

  // Handle period change
  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.00';
    }
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Print handler
  const handlePrint = () => {
    window.print();
  };

  if (loading && !expenseData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error && !expenseData) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchData}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!expenseData) {
    return (
      <Box>
        <Alert severity="info">No data available. Please select a period and refresh.</Alert>
        <Button variant="contained" onClick={fetchData} sx={{ mt: 2 }}>
          Load Data
        </Button>
      </Box>
    );
  }

  // Get top vendors
  const topVendors = Object.entries(expenseData.byVendor || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={selectedPeriod}
            onChange={handlePeriodChange}
            label="Period"
          >
            <MenuItem value="current">Current Month</MenuItem>
            <MenuItem value="quarter">Current Quarter</MenuItem>
            <MenuItem value="year">Current Year</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>
        
        {selectedPeriod === 'custom' && (
          <>
            <TextField
              label="Start Date"
              type="date"
              value={startDate.toISOString().split('T')[0]}
              onChange={(e) => setStartDate(new Date(e.target.value))}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate.toISOString().split('T')[0]}
              onChange={(e) => setEndDate(new Date(e.target.value))}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </>
        )}
        
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
        >
          Refresh Data
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Expenses
                  </Typography>
                  <Typography variant="h4" color="error">
                    ₵{formatCurrency(expenseData.summary.totalExpenses)}
                  </Typography>
                </Box>
                <TrendingDownIcon color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Number of Expenses
              </Typography>
              <Typography variant="h4">
                {expenseData.summary.expenseCount}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Average: ₵{formatCurrency(expenseData.summary.averageExpense)} per expense
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Period
              </Typography>
              <Typography variant="body1">
                {new Date(expenseData.period.start).toLocaleDateString()} - {new Date(expenseData.period.end).toLocaleDateString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Staff Salaries Breakdown */}
      {expenseData.staffSalaries && expenseData.staffSalaries.total > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'warning.light' }}>
            <Typography variant="h6" gutterBottom>
              Staff Salaries Breakdown
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total: ₵{formatCurrency(expenseData.staffSalaries.total)} | 
              Base: ₵{formatCurrency(expenseData.staffSalaries.totalBase)} | 
              Bonuses: ₵{formatCurrency(expenseData.staffSalaries.totalBonuses)} | 
              Staff: {expenseData.staffSalaries.staffCount}
            </Typography>
          </Box>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Position</strong></TableCell>
                  <TableCell align="right"><strong>Staff Count</strong></TableCell>
                  <TableCell align="right"><strong>Base Salary</strong></TableCell>
                  <TableCell align="right"><strong>Bonuses</strong></TableCell>
                  <TableCell align="right"><strong>Total</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(expenseData.staffSalaries.byPosition)
                  .sort((a, b) => b[1].total - a[1].total)
                  .map(([position, data]) => (
                    <TableRow key={position}>
                      <TableCell>{position}</TableCell>
                      <TableCell align="right">{data.count}</TableCell>
                      <TableCell align="right">{formatCurrency(data.base)}</TableCell>
                      <TableCell align="right">{formatCurrency(data.bonuses)}</TableCell>
                      <TableCell align="right">
                        <strong>₵{formatCurrency(data.total)}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>TOTAL</strong></TableCell>
                  <TableCell align="right"><strong>{expenseData.staffSalaries.staffCount}</strong></TableCell>
                  <TableCell align="right"><strong>₵{formatCurrency(expenseData.staffSalaries.totalBase)}</strong></TableCell>
                  <TableCell align="right"><strong>₵{formatCurrency(expenseData.staffSalaries.totalBonuses)}</strong></TableCell>
                  <TableCell align="right">
                    <strong>₵{formatCurrency(expenseData.staffSalaries.total)}</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Expenses by Category */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>
            Expenses by Category
          </Typography>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell align="right"><strong>Amount (₵)</strong></TableCell>
                <TableCell align="right"><strong>Percentage</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(expenseData.byCategory || {})
                .filter(([category, amount]) => amount > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([category, amount]) => {
                  const percentage = expenseData.summary.totalExpenses > 0
                    ? ((amount / expenseData.summary.totalExpenses) * 100).toFixed(1)
                    : '0.0';
                  
                  return (
                    <TableRow key={category}>
                      <TableCell>{category}</TableCell>
                      <TableCell align="right">
                        <strong>₵{formatCurrency(amount)}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={`${percentage}%`}
                          color={parseFloat(percentage) > 20 ? 'error' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>TOTAL</strong></TableCell>
                <TableCell align="right">
                  <strong>₵{formatCurrency(expenseData.summary.totalExpenses)}</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>100%</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Top Vendors */}
      {topVendors.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Top Vendors / Recipients
            </Typography>
          </Box>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Vendor / Recipient</strong></TableCell>
                  <TableCell align="right"><strong>Amount (₵)</strong></TableCell>
                  <TableCell align="right"><strong>Percentage</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topVendors.map(([vendor, amount]) => {
                  const percentage = expenseData.summary.totalExpenses > 0
                    ? ((amount / expenseData.summary.totalExpenses) * 100).toFixed(1)
                    : '0.0';
                  
                  return (
                    <TableRow key={vendor}>
                      <TableCell>{vendor}</TableCell>
                      <TableCell align="right">₵{formatCurrency(amount)}</TableCell>
                      <TableCell align="right">{percentage}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Expenses by Term */}
      {expenseData.byTerm && Object.keys(expenseData.byTerm).length > 0 && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Expenses by Term
            </Typography>
          </Box>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Term</strong></TableCell>
                  <TableCell align="right"><strong>Amount (₵)</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(expenseData.byTerm)
                  .sort((a, b) => b[1] - a[1])
                  .map(([term, amount]) => (
                    <TableRow key={term}>
                      <TableCell>{term}</TableCell>
                      <TableCell align="right">₵{formatCurrency(amount)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default ExpensesTab;
